const PRODUCT_SEARCH = 'hoyry/product-search';
const TRACK_ROOT = '[data-hoyry-track], [data-burst-goal]';

/**
 * GTM/dataLayer: only when statistics cookies are allowed.
 * No Cookiebot → allow. Cookiebot present but no response yet → deny GTM.
 */
function hasAnalyticsConsent() {
	const cookiebot = window.Cookiebot;
	if ( ! cookiebot || typeof cookiebot.consent !== 'object' ) {
		return true;
	}

	// Banner unanswered — do not push to GTM.
	if ( cookiebot.hasResponse !== true ) {
		return false;
	}

	return cookiebot.consent.statistics === true;
}

/**
 * WP hit consent flag: 1 accepted, 0 denied, -1 unknown (no Cookiebot / pending).
 */
function consentValue() {
	const cookiebot = window.Cookiebot;
	if ( ! cookiebot || typeof cookiebot.consent !== 'object' ) {
		return -1;
	}

	// Pending banner answer is unknown; explicit deny (incl. statistics off) is 0.
	if ( cookiebot.hasResponse !== true ) {
		return -1;
	}

	return cookiebot.consent.statistics === true ? 1 : 0;
}

const TRAFFIC_STORAGE_KEY = 'hoyry_analytics_traffic';
const AD_CLICK_PARAMS = [ 'gclid', 'fbclid', 'msclkid', 'ttclid', 'wbraid', 'gbraid' ];

/**
 * Sanitize UTM-like string: lowercase, safe charset, max 64.
 */
function sanitizeUtm( value ) {
	return String( value || '' )
		.toLowerCase()
		.replace( /[^a-z0-9._+-]/g, '' )
		.slice( 0, 64 );
}

/**
 * External hostname only, or "direct" for empty / same-site.
 */
function sanitizeReferrerHost( referrerUrl ) {
	const raw = String( referrerUrl || '' ).trim();
	if ( ! raw || raw === 'direct' ) {
		return 'direct';
	}

	try {
		const withScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test( raw ) || raw.startsWith( '//' )
			? raw
			: `https://${ raw }`;
		const ref = new URL( withScheme, window.location.href );
		let host = ( ref.hostname || '' ).toLowerCase();
		host = host.replace( /[^a-z0-9.-]/g, '' ).replace( /^\.+|\.+$/g, '' );
		if ( ! host || ! host.includes( '.' ) ) {
			return 'direct';
		}
		const site = ( window.location.hostname || '' ).toLowerCase().replace( /^www\./, '' );
		const norm = host.replace( /^www\./, '' );
		if ( norm === site ) {
			return 'direct';
		}
		return host.slice( 0, 253 );
	} catch ( e ) {
		return 'direct';
	}
}

/**
 * Capture landing attribution once per tab session (first page view wins).
 */
function captureTrafficAttribution() {
	try {
		if ( sessionStorage.getItem( TRAFFIC_STORAGE_KEY ) ) {
			return;
		}
	} catch ( e ) {
		if ( window.__hoyryAnalyticsTraffic ) {
			return;
		}
	}

	const params = new URLSearchParams( window.location.search || '' );
	const attribution = {
		referrer: sanitizeReferrerHost( document.referrer ),
		utm_source: sanitizeUtm( params.get( 'utm_source' ) ),
		utm_medium: sanitizeUtm( params.get( 'utm_medium' ) ),
		utm_campaign: sanitizeUtm( params.get( 'utm_campaign' ) ),
		ads: AD_CLICK_PARAMS.some( ( name ) => params.has( name ) ),
	};

	try {
		sessionStorage.setItem( TRAFFIC_STORAGE_KEY, JSON.stringify( attribution ) );
	} catch ( e ) {
		window.__hoyryAnalyticsTraffic = attribution;
	}
}

/**
 * @returns {{ referrer: string, utm_source: string, utm_medium: string, utm_campaign: string, ads: boolean }}
 */
function getTrafficAttribution() {
	let stored = null;
	try {
		const raw = sessionStorage.getItem( TRAFFIC_STORAGE_KEY );
		if ( raw ) {
			stored = JSON.parse( raw );
		}
	} catch ( e ) {
		stored = window.__hoyryAnalyticsTraffic || null;
	}

	if ( ! stored || typeof stored !== 'object' ) {
		return {
			referrer: 'direct',
			utm_source: '',
			utm_medium: '',
			utm_campaign: '',
			ads: false,
		};
	}

	const host = String( stored.referrer || 'direct' ).toLowerCase();
	return {
		referrer: host === 'direct' ? 'direct' : sanitizeReferrerHost( host ),
		utm_source: sanitizeUtm( stored.utm_source ),
		utm_medium: sanitizeUtm( stored.utm_medium ),
		utm_campaign: sanitizeUtm( stored.utm_campaign ),
		ads: Boolean( stored.ads ),
	};
}

/** Hidden field names for Fluent Forms server-side attribution. */
const FLUENT_ATTR_FIELDS = {
	_hoyry_ref: 'referrer',
	_hoyry_utm_source: 'utm_source',
	_hoyry_utm_medium: 'utm_medium',
	_hoyry_utm_campaign: 'utm_campaign',
	_hoyry_ads: 'ads',
};

function ensureFluentAttributionFields( form ) {
	if ( ! form || form.nodeName !== 'FORM' ) {
		return;
	}

	const attr = getTrafficAttribution();
	Object.keys( FLUENT_ATTR_FIELDS ).forEach( ( name ) => {
		let input = form.querySelector( `input[name="${ name }"]` );
		if ( ! input ) {
			input = document.createElement( 'input' );
			input.type = 'hidden';
			input.name = name;
			form.appendChild( input );
		}
		const key = FLUENT_ATTR_FIELDS[ name ];
		input.value = key === 'ads' ? ( attr.ads ? '1' : '0' ) : String( attr[ key ] ?? '' );
	} );
}

function injectFluentAttributionFields() {
	document.querySelectorAll( 'form.frm-fluent-form, form[data-form_id], .fluentform form' ).forEach( ( form ) => {
		ensureFluentAttributionFields( form );
	} );
}

function restConfig() {
	return {
		restUrl: window.hoyryAnalytics?.restUrl || '/wp-json/hoyry-analytics/v1',
		nonce: window.hoyryAnalytics?.nonce || '',
	};
}

/**
 * Fire-and-forget POST that must survive navigation (CTA click → new page).
 * sendBeacon cannot set X-WP-Nonce; the nonce is passed as `_wpnonce` (query).
 * Fallback keeps the same JSON body and header auth via keepalive fetch.
 */
function postJson( path, body ) {
	const { restUrl, nonce } = restConfig();
	const url = `${ restUrl }${ path }`;
	const json = JSON.stringify( body );

	if ( typeof navigator.sendBeacon === 'function' ) {
		const separator = url.includes( '?' ) ? '&' : '?';
		const beaconUrl = `${ url }${ separator }_wpnonce=${ encodeURIComponent( nonce ) }`;
		try {
			const queued = navigator.sendBeacon(
				beaconUrl,
				new Blob( [ json ], { type: 'application/json' } )
			);
			if ( queued ) {
				return;
			}
		} catch ( e ) {
			// Fall through to keepalive fetch.
		}
	}

	return fetch( url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': nonce,
		},
		credentials: 'same-origin',
		keepalive: true,
		body: json,
	} ).catch( () => {} );
}

function pushToDataLayer( payload ) {
	const goalId = payload.goalId || '';

	if ( hasAnalyticsConsent() ) {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push( {
			event: 'hoyry_cta',
			hoyry_label: payload.label || '',
			hoyry_block: payload.block || '',
			hoyry_link_url: payload.linkUrl || '',
			hoyry_track: payload.track || 'cta_click',
			eventCallback: goalId ? () => postGtmAck( goalId ) : undefined,
		} );
	}

	postGoalHit( payload );
}

function postGoalHit( payload ) {
	const goalId = payload.goalId || '';
	if ( ! goalId ) {
		return;
	}

	const attr = getTrafficAttribution();
	postJson( '/hit', {
		goal_id: goalId,
		consent: consentValue(),
		referrer: attr.referrer,
		utm_source: attr.utm_source,
		utm_medium: attr.utm_medium,
		utm_campaign: attr.utm_campaign,
		ads: attr.ads ? 1 : 0,
	} );
}

function postGtmAck( goalId ) {
	if ( ! goalId ) {
		return;
	}

	postJson( '/gtm-ack', {
		goal_id: goalId,
	} );
}

function postGtmAckForForm( formId ) {
	if ( ! formId ) {
		return;
	}

	postJson( '/gtm-ack', {
		form_id: String( formId ),
	} );
}

function textOf( element ) {
	return ( element?.textContent || '' ).replace( /\s+/g, ' ' ).trim().slice( 0, 120 );
}

function payloadFromRoot( root, clickable ) {
	const label =
		root.getAttribute( 'data-hoyry-track-label' ) ||
		textOf( clickable ) ||
		textOf( root );

	let linkUrl = '';
	if ( clickable instanceof HTMLAnchorElement && clickable.href ) {
		linkUrl = clickable.href;
	} else if ( clickable?.getAttribute?.( 'href' ) ) {
		linkUrl = clickable.getAttribute( 'href' );
	}

	return {
		track: root.getAttribute( 'data-hoyry-track' ) || 'cta_click',
		label,
		block: root.getAttribute( 'data-hoyry-track-block' ) || '',
		linkUrl,
		goalId: root.getAttribute( 'data-hoyry-goal-id' ) || '',
	};
}

function trackRoot( target ) {
	return target?.closest?.( TRACK_ROOT ) || null;
}

function track( payload ) {
	if ( ! payload || typeof payload !== 'object' ) {
		return;
	}

	pushToDataLayer( {
		track: payload.track || payload.hoyry_track || 'cta_click',
		label: payload.label || payload.hoyry_label || '',
		block: payload.block || payload.hoyry_block || '',
		linkUrl: payload.linkUrl || payload.hoyry_link_url || '',
		goalId: payload.goalId || '',
	} );
}

function isProductSearch( root ) {
	return root.getAttribute( 'data-hoyry-track-block' ) === PRODUCT_SEARCH;
}

function onClick( event ) {
	const root = trackRoot( event.target );
	if ( ! root ) {
		return;
	}

	if ( isProductSearch( root ) ) {
		if ( ! event.target.closest( '.hoyry-product-search-show-all' ) ) {
			return;
		}
		track( payloadFromRoot( root, event.target ) );
		return;
	}

	const clickable = event.target.closest( 'a, button, input[type="submit"], input[type="button"]' );
	track( payloadFromRoot( root, clickable || event.target ) );
}

function onSearchEnter( event ) {
	if ( event.key !== 'Enter' ) {
		return;
	}

	const input = event.target.closest?.( '.hoyry-product-search-input' );
	if ( ! input ) {
		return;
	}

	const root = trackRoot( input );
	if ( ! root || ! isProductSearch( root ) ) {
		return;
	}

	track( payloadFromRoot( root, input ) );
}

function onSubmit( event ) {
	const root = trackRoot( event.target );
	if ( ! root || ! isProductSearch( root ) ) {
		return;
	}

	track( payloadFromRoot( root, event.submitter || event.target ) );
}

/**
 * Fluent CustomEvent detail is { form, config, response }, not { form_id }.
 * Form element has data-form_id (see Fluent FormBuilder).
 */
function formIdFromFluent( detail ) {
	if ( ! detail || typeof detail !== 'object' ) {
		return '';
	}

	const formEl = detail.form;
	const fromDom =
		( formEl && typeof formEl.getAttribute === 'function'
			? formEl.getAttribute( 'data-form_id' )
			: '' ) ||
		formEl?.dataset?.form_id ||
		formEl?.dataset?.formId ||
		'';

	const config = detail.config || {};
	const response = detail.response || {};
	const fromConfig = config.id || config.form_id || config.formId || '';
	const fromResponse =
		response.form_id ||
		response.formId ||
		response.data?.form_id ||
		response.data?.formId ||
		'';

	const raw =
		fromDom ||
		fromConfig ||
		fromResponse ||
		detail.form_id ||
		detail.formId ||
		detail.id ||
		'';

	const id = parseInt( String( raw ), 10 );
	return id > 0 ? String( id ) : '';
}

function postFluentHitAttribution( formId ) {
	if ( ! formId ) {
		return;
	}

	const attr = getTrafficAttribution();
	postJson( '/hit-attribution', {
		form_id: String( formId ),
		referrer: attr.referrer,
		utm_source: attr.utm_source,
		utm_medium: attr.utm_medium,
		utm_campaign: attr.utm_campaign,
		ads: attr.ads ? 1 : 0,
	} );
}

function pushFluentFormSubmit( detail ) {
	const formId = formIdFromFluent( detail );
	if ( ! formId ) {
		return;
	}

	if ( hasAnalyticsConsent() ) {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push( {
			event: 'hoyry_form_submit',
			form_id: formId,
			eventCallback: () => postGtmAckForForm( formId ),
		} );
	}

	// Server already counted the hit; send sessionStorage attribution only.
	postFluentHitAttribution( formId );
}

window.hoyry = window.hoyry || {};
window.hoyry.track = track;

captureTrafficAttribution();
injectFluentAttributionFields();

document.addEventListener( 'click', onClick, true );
document.addEventListener( 'keydown', onSearchEnter, true );
document.addEventListener( 'submit', ( event ) => {
	onSubmit( event );
	const form = event.target;
	if ( form && form.nodeName === 'FORM' ) {
		ensureFluentAttributionFields( form );
	}
}, true );

document.addEventListener( 'fluentform_submission_success', ( event ) => {
	pushFluentFormSubmit( event.detail || {} );
} );

document.addEventListener( 'fluentform_init', () => {
	injectFluentAttributionFields();
} );

if ( window.jQuery ) {
	window.jQuery( document.body ).on(
		'fluentform_submission_success',
		( _event, detail ) => {
			pushFluentFormSubmit( detail || {} );
		}
	);
	window.jQuery( document ).on( 'fluentform_init', () => {
		injectFluentAttributionFields();
	} );
}

// Fluent may render forms async; refresh hidden fields when DOM grows.
if ( typeof MutationObserver === 'function' ) {
	let scheduled = null;
	const observer = new MutationObserver( () => {
		if ( scheduled ) {
			return;
		}
		scheduled = setTimeout( () => {
			scheduled = null;
			injectFluentAttributionFields();
		}, 200 );
	} );
	observer.observe( document.documentElement, { childList: true, subtree: true } );
}
